import { ParentHandbookWarningBanner, Trans } from '@wonderschool/ccms-ui';
import { Badge, Button, ButtonLink, ColorPaletteEnum, Dialog, WidgetSizeEnum } from '@wonderschool/common-base-ui';
import { ParentHandbookQuery } from '@wonderschool/common-food-program-data-access';
import { useTranslation } from 'react-i18next';
import { ReactComponent as ParentHandbookImage } from '../../../assets/images/parent-handbook.svg';
import { RouteNameEnum, useRoutes } from '../../../navigation';

type ParentHandbookModalProps = {
  isModalOpen: boolean;
  closeModal: () => void;
  onExistingParentHandbook: () => void;
  marketplaceSchoolId?: NonNullable<ParentHandbookQuery['parentHandbook']>['schoolId'];
};

const ParentHandbookModal = ({
  isModalOpen,
  closeModal,
  onExistingParentHandbook,
  marketplaceSchoolId,
}: ParentHandbookModalProps) => {
  const { t } = useTranslation();
  const newLabel = `${t('common.new')}!`.toUpperCase();
  const { gotoRouteByName } = useRoutes();

  const handleGetStarted = () => {
    gotoRouteByName(RouteNameEnum.PARENT_HANDBOOK);
  };

  return (
    <Dialog
      isOpen={isModalOpen}
      onClose={closeModal}
      fullScreenOnMobile={true}
      extraDialogWrapperClasses="sm:max-w-2xl w-full"
    >
      <Dialog.Title>
        <div className="flex flex-col gap-2">
          <div className="flex w-full">
            <ParentHandbookImage width="100%" height="auto" />
          </div>
          <div className="flex flex-col gap-2 px-6 py-4 text-2xl font-bold">
            <div>
              <Badge
                color={ColorPaletteEnum.YELLOW}
                label={newLabel}
                isCircleBadge
                extraContainerClasses="px-3 bg-yellow-200 ring-yellow-200"
              />
            </div>
            <h3>{t('parentHandbook.modal.title')}</h3>
          </div>
        </div>
      </Dialog.Title>
      <Dialog.Description>
        <div className="space-y-3 px-6 text-base text-gray-800">
          <p>{t('parentHandbook.modal.description')}</p>
          {marketplaceSchoolId && (
            <>
              <p>
                &middot; <Trans i18nKey="parentHandbook.modal.maximizeYourIncome" components={{ strong: <strong /> }} />
              </p>
              <p>
                &middot;{' '}
                <Trans i18nKey="parentHandbook.modal.takeControlSchedule" components={{ strong: <strong /> }} />
              </p>
              <p>
                &middot;{' '}
                <Trans i18nKey="parentHandbook.modal.saveTimeCommunication" components={{ strong: <strong /> }} />
              </p>
              <p>
                &middot; <Trans i18nKey="parentHandbook.modal.focusOnWhatMatters" components={{ strong: <strong /> }} />
              </p>
              <p>{t('parentHandbook.modal.maintainDescription')}</p>{' '}
            </>
          )}
        </div>
      </Dialog.Description>
      <Dialog.Actions>
        <div className="fixed bottom-0 flex flex-col gap-8 md:relative md:bottom-auto md:w-full md:pt-6">
          <ParentHandbookWarningBanner marketplaceSchoolId={marketplaceSchoolId} />

          <div className="flex flex-col gap-4">
            <Button
              primary
              onClick={handleGetStarted}
              data-testid="get-started-btn"
              size={WidgetSizeEnum.MEDIUM}
              disabled={!marketplaceSchoolId}
            >
              {t('common.getStarted')}
            </Button>

            <ButtonLink
              onClick={onExistingParentHandbook}
              primary={false}
              content={t('parentHandbook.modal.alreadyHaveParentHandbookButton')}
              extraClasses="md:px-4"
              size={WidgetSizeEnum.MEDIUM}
            />
          </div>
        </div>
      </Dialog.Actions>
    </Dialog>
  );
};

export default ParentHandbookModal;
